import React, { useEffect, useState, useCallback } from "react";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import "../../stiluri/DosarSocial.css";

const DosarSocial = ({ id }) => {
  const storage = getStorage();
  const db = getFirestore();
  const auth = getAuth();

  const rootPath = `${id}/dosar_social/`;

  const [tree, setTree] = useState(null);
  const [expandedPaths, setExpandedPaths] = useState(new Set([rootPath]));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(rootPath);

  // Ștergere cu motiv
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [motivStergere, setMotivStergere] = useState("");

  // Audit
  const [showAudit, setShowAudit] = useState(true); // devine true la mount
  const [auditRecords, setAuditRecords] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [motivUpload, setMotivUpload] = useState("");


  // Paginare audit
  const [auditPage, setAuditPage] = useState(1);
  const pageSize = 10;

  // --- Load folder structure ---
  const loadFolderStructure = useCallback(
    async (path) => {
      try {
        const folderRef = ref(storage, path);
        const res = await listAll(folderRef);

        const folders = await Promise.all(
          res.prefixes.map((folderRef) => loadFolderStructure(folderRef.fullPath))
        );

        const files = res.items.map((itemRef) => ({
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url: null,
        }));

        return {
          name: path.split("/").filter(Boolean).pop() || "root",
          path,
          folders,
          files,
        };
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    [storage]
  );

  const loadFilesForNode = async (node) => {
    const folderRef = ref(storage, node.path);
    const res = await listAll(folderRef);
    const files = await Promise.all(
      res.items.map(async (itemRef) => ({
        name: itemRef.name,
        fullPath: itemRef.fullPath,
        url: null,
      }))
    );
    return files;
  };

  const expandNode = async (path, currentNode) => {
    setExpandedPaths((prev) => new Set(prev).add(path));

    if (currentNode && currentNode.files.length === 0) {
      const files = await loadFilesForNode(currentNode);
      const updateTree = (node) => {
        if (node.path === path) {
          return { ...node, files };
        }
        return {
          ...node,
          folders: node.folders.map(updateTree),
        };
      };
      setTree((prevTree) => updateTree(prevTree));
    }
  };

  // --- Audit logging ---
  const logAudit = async ({ actiune, fisierNume, folder, motiv = null }) => {
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, "IstoricDosare"), {
        beneficiarId: id,
        componenta: "dosar_social",
        actiune,
        fisierNume,
        folder,
        timestamp: new Date(),
        userEmail: user ? user.email : "anonim",
        motivStergere: motiv || null,
      });
    } catch (e) {
      console.error("Eroare la log audit:", e);
    }
  };

  const handlePreview = async (file) => {
    if (!file.url) {
      const url = await getDownloadURL(ref(storage, file.fullPath));
      setPreview({ ...file, url });
    } else {
      setPreview(file);
    }
  };

  useEffect(() => {
    if (!id) {
      setError("ID-ul beneficiarului nu este definit.");
      return;
    }
    setLoading(true);
    loadFolderStructure(rootPath)
      .then((data) => {
        setTree(data);
        setSelectedFolder(rootPath);
        setLoading(false);
        setError(null);
      })
      .catch(() => {
        setError("Eroare la încărcarea dosarului.");
        setLoading(false);
      });
  }, [id, loadFolderStructure, rootPath]);

  // --- Încarcă auditul la mount ---
  useEffect(() => {
    if (!id) return;

    const fetchAudit = async () => {
      setLoadingAudit(true);
      try {
        const q = query(
          collection(db, "IstoricDosare"),
          where("beneficiarId", "==", id),
          where("componenta", "==", "dosar_social"),
          orderBy("timestamp", "desc")
        );
        const querySnapshot = await getDocs(q);
        const records = [];
        querySnapshot.forEach((doc) => {
          records.push({ id: doc.id, ...doc.data() });
        });
        setAuditRecords(records);
        setShowAudit(true);
      } catch (e) {
        alert("Eroare la încărcarea auditului.");
        console.error(e);
      }
      setLoadingAudit(false);
    };

    fetchAudit();
  }, [id, db]);

  const toggleAudit = () => {
    setShowAudit((prev) => !prev);
  };

  const getAllFolders = (node) => {
    if (!node) return [];
    let foldersList = [{ name: node.name, path: node.path }];
    node.folders.forEach((folder) => {
      foldersList = foldersList.concat(getAllFolders(folder));
    });
    return foldersList;
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      alert("Alege cel puțin un fișier!");
      return;
    }
    setUploading(true);
    try {
      const pathWithSlash = selectedFolder.endsWith("/")
        ? selectedFolder
        : selectedFolder + "/";

      await Promise.all(
        selectedFiles.map(async (file) => {
          const fileRef = ref(storage, pathWithSlash + file.name);
          await uploadBytes(fileRef, file);
          await logAudit({
            actiune: "upload",
            fisierNume: file.name,
            folder: pathWithSlash,
                  motiv: motivUpload.trim() || null, // trim și fallback null

          });
        })
      );

      alert("Încărcare reușită!");
      await expandNode(selectedFolder, tree);

      setSelectedFiles([]);
      setShowUploadModal(false);
      setMotivUpload("");

    } catch (err) {
      alert("Eroare la upload.");
      console.error(err);
    }
    setUploading(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
  };

  // Paginare audit:
  const totalPages = Math.ceil(auditRecords.length / pageSize);
  const paginatedRecords = auditRecords.slice(
    (auditPage - 1) * pageSize,
    auditPage * pageSize
  );

  const prevPage = () => {
    setAuditPage((p) => Math.max(1, p - 1));
  };

  const nextPage = () => {
    setAuditPage((p) => Math.min(totalPages, p + 1));
  };

  const toggleExpand = async (path, node) => {
    if (expandedPaths.has(path)) {
      setExpandedPaths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(path);
        return newSet;
      });
    } else {
      await expandNode(path, node);
    }
  };

  // Ștergere cu motiv
  const initiateDeleteFile = (file) => {
    setFileToDelete(file);
    setMotivStergere("");
    setShowDeleteModal(true);
  };

  const confirmDeleteFile = async () => {
    if (!motivStergere.trim()) {
      alert("Trebuie să introduci motivul ștergerii!");
      return;
    }
    try {
      const fileRef = ref(storage, fileToDelete.fullPath);
      await deleteObject(fileRef);
      alert("Fișier șters cu succes!");
      await expandNode(
        fileToDelete.fullPath.substring(0, fileToDelete.fullPath.lastIndexOf("/")),
        tree
      );
      setPreview(null);
      await logAudit({
        actiune: "delete",
        fisierNume: fileToDelete.name,
        folder: fileToDelete.fullPath.substring(0, fileToDelete.fullPath.lastIndexOf("/")),
        motiv: motivStergere.trim(),
      });
    } catch (err) {
      alert("Eroare la ștergerea fișierului.");
      console.error(err);
    }
    setShowDeleteModal(false);
    setFileToDelete(null);
    setMotivStergere("");
  };

  const renderNode = (node) => {
    if (!node) return null;

    const isExpanded = expandedPaths.has(node.path);

    return (
      <div className="folder" key={node.path}>
        <div
          className="folder-name"
          onClick={() => toggleExpand(node.path, node)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") toggleExpand(node.path, node);
          }}
        >
          {isExpanded ? "📂" : "📁"} {node.name}
        </div>

        {isExpanded && (
          <div className="folder-contents">
            {node.folders.map((folder) => renderNode(folder))}
            {node.files.map((file) => (
              <div className="file" key={file.fullPath}>
                <span
                  className="file-name"
                  onClick={() => handlePreview(file)}
                  role="button"
                  tabIndex={0}
                >
                  📄 {file.name}
                </span>
                <button
                  className="preview-btn"
                  onClick={() => handlePreview(file)}
                  title="Previzualizează"
                >
                  👁️
                </button>
                <a
                  href={file.url || "#"}
                  onClick={(e) => {
                    if (!file.url) e.preventDefault();
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  ⬇️
                </a>
                <button
                  className="delete-btn"
                  onClick={() => initiateDeleteFile(file)}
                  title="Șterge"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="dosar-social-container">
        <h2>Dosar Social</h2>
        {loading && <p>Se încarcă dosarul...</p>}
        {error && <p className="error-msg">{error}</p>}
        {tree ? renderNode(tree) : <p>Te rugăm să aștepți.</p>}

        <button
          className="upload-btn"
          onClick={() => setShowUploadModal(true)}
          style={{ marginTop: "1rem", marginRight: "1rem" }}
        >
          Încarcă fișier
        </button>

        <button
          className="audit-btn"
          onClick={toggleAudit}
          style={{ marginTop: "1rem" }}
        >
          {showAudit ? "Închide audit" : "Vezi audit"}
        </button>

        {/* Modal Upload */}
        {showUploadModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowUploadModal(false)}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              style={{
                border: "2px dashed #888",
                padding: "20px",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <button
                className="close-btn"
                onClick={() => setShowUploadModal(false)}
              >
                ×
              </button>
              
              <h3>Încarcă fișier</h3>
              <p>Trage fișierele aici sau folosește selectorul de fișiere.</p>
              

              <form onSubmit={handleUploadSubmit}>
                <label htmlFor="folderSelect">Folder:</label>
                <select
                  id="folderSelect"
                  value={selectedFolder}
                  onChange={(e) => setSelectedFolder(e.target.value)}
                >
                  
                  {tree &&
                    getAllFolders(tree).map((folder) => (
                      <option key={folder.path} value={folder.path}>
                        {folder.path}
                      </option>
                    ))}
                </select>

                <input
                  type="file"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  required={selectedFiles.length === 0}
                  disabled={uploading}
                  style={{ marginTop: "1rem" }}
                />
                <label htmlFor="motivUpload">Motiv încărcare (opțional):</label>
<textarea
  id="motivUpload"
  value={motivUpload}
  onChange={(e) => setMotivUpload(e.target.value)}
  rows={3}
  style={{ width: "100%", marginTop: "1rem" }}
/>

                {selectedFiles.length > 0 && (
                  <p>{selectedFiles.length} fișier(e) selectat(e)</p>
                )}
                <button type="submit" disabled={uploading}>
                  {uploading ? "Se încarcă..." : "Încarcă"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Modal Delete cu motiv */}
        {showDeleteModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowDeleteModal(false)}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
              style={{ padding: "20px", maxWidth: "400px" }}
            >
              <h3>Confirmă ștergerea</h3>
              <p>
                Ștergi fișierul: <strong>{fileToDelete?.name}</strong>
              </p>
              <label htmlFor="motivStergere">Motiv ștergere (obligatoriu):</label>
              <textarea
                id="motivStergere"
                value={motivStergere}
                onChange={(e) => setMotivStergere(e.target.value)}
                rows={4}
                style={{ width: "100%" }}
                required
              />
              <div style={{ marginTop: "1rem", textAlign: "right" }}>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  style={{ marginRight: "1rem" }}
                >
                  Anulează
                </button>
                <button onClick={confirmDeleteFile}>Șterge</button>
              </div>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="preview-overlay" onClick={() => setPreview(null)}>
            <div
              className="preview-content"
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
            >
              <button className="close-btn" onClick={() => setPreview(null)}>
                ×
              </button>
              <h3>{preview.name}</h3>
              {preview.url ? (
                <>
                  {preview.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                    <img
                      src={preview.url}
                      alt={preview.name}
                      className="preview-image"
                    />
                  ) : preview.name.match(/\.pdf$/i) ? (
                    <iframe
                      src={preview.url}
                      title={preview.name}
                      className="preview-pdf"
                    />
                  ) : (
                    <p>Preview indisponibil.</p>
                  )}
                </>
              ) : (
                <p>Se încarcă...</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Audit tabel separat, dedesubt */}
      {showAudit && (
        <div
          className="audit-table-container"
          style={{
            marginTop: "1rem",
            padding: "1rem",
            backgroundColor: "#f9f9f9",
            border: "1px solid #ccc",
            borderRadius: "6px",
            width: "100%",
          }}
        >
          <h3>Audit Dosar Social</h3>
          {loadingAudit ? (
            <p>Se încarcă auditul...</p>
          ) : auditRecords.length === 0 ? (
            <p>Nu există înregistrări de audit.</p>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #ccc" }}>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>
                      Data / Ora
                    </th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>
                      Acțiune
                    </th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>
                      Fișier
                    </th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>
                      Folder
                    </th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>
                      Utilizator
                    </th>
                    <th style={{ textAlign: "left", padding: "0.5rem" }}>
                      Motiv
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((rec) => (
                    <tr key={rec.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "0.5rem" }}>
                        {rec.timestamp?.toDate
                          ? rec.timestamp.toDate().toLocaleString()
                          : new Date(rec.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: "0.5rem" }}>{rec.actiune}</td>
                      <td style={{ padding: "0.5rem" }}>{rec.fisierNume}</td>
                      <td style={{ padding: "0.5rem" }}>{rec.folder}</td>
                      <td style={{ padding: "0.5rem" }}>{rec.userEmail}</td>
                      <td style={{ padding: "0.5rem", whiteSpace: "pre-wrap" }}>
                        {rec.motivStergere || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                style={{
                  marginTop: "1rem",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <button onClick={prevPage} disabled={auditPage === 1}>
                  ← Înapoi
                </button>
                <span>
                  Pagina {auditPage} din {totalPages}
                </span>
                <button onClick={nextPage} disabled={auditPage === totalPages}>
                  Înainte →
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DosarSocial;
